// The home page's key chart (Oct 2026): every key the library is written in,
// placed on the circle of fifths — majors round the outside, each relative
// minor just inside the major that shares its key signature (a minor inside
// C, d inside F), minors lowercase as the analysis lane writes them. Pure
// geometry and naming; KeyWheel.tsx draws it.

import type { LibraryStatsKey } from '@/music/libraryStats';

/** A pitch class's place on the circle of fifths: 0 = C at the top, clockwise. */
export const fifthsIndex = (pc: number) => (((pc * 7) % 12) + 12) % 12;

const MAJOR_NAMES = ['C', 'G', 'D', 'A', 'E', 'B', 'F♯', 'D♭', 'A♭', 'E♭', 'B♭', 'F'];
const MINOR_NAMES = ['a', 'e', 'b', 'f♯', 'c♯', 'g♯', 'e♭', 'b♭', 'f', 'c', 'g', 'd'];

export interface WheelSlot {
  /** Position round the circle, 0 = the top. */
  slot: number;
  minor: boolean;
  /** What the wheel prints: "B♭", "c". */
  short: string;
  /** What a list heading or a screen reader says: "B♭ major", "C minor". */
  name: string;
  /** The tunes in this key, every spelling of it together. */
  tunes: number[];
}

const rootOf = (key: LibraryStatsKey) => key.label.replace(/\s*minor$/i, '').trim();

/**
 * All 24 places on the wheel, empty ones included so the circle reads whole.
 * Enharmonic spellings share a place (F♯ and G♭ major): their tunes join, the
 * commoner spelling labels it, and the name gives both.
 */
export function keyWheelSlots(keys: readonly LibraryStatsKey[]): WheelSlot[] {
  const slots: WheelSlot[] = [];
  for (const minor of [false, true]) {
    for (let slot = 0; slot < 12; slot++) {
      // A minor key sits with its relative major, three semitones up.
      const here = keys
        .filter((key) => key.minor === minor && fifthsIndex(minor ? key.pc + 3 : key.pc) === slot)
        .sort((a, b) => b.tunes.length - a.tunes.length || a.label.localeCompare(b.label, 'en'));
      const roots = here.map(rootOf);
      const quality = minor ? 'minor' : 'major';
      const short = roots.length
        ? minor
          ? roots[0].toLowerCase()
          : roots[0]
        : minor
          ? MINOR_NAMES[slot]
          : MAJOR_NAMES[slot];
      const fallback = minor ? MINOR_NAMES[slot].toUpperCase() : MAJOR_NAMES[slot];
      slots.push({
        slot,
        minor,
        short,
        name: `${roots.length ? roots.join(' / ') : fallback} ${quality}`,
        tunes: here.flatMap((key) => key.tunes),
      });
    }
  }
  return slots;
}

/** The diameter of a slot's dot, as a percentage of the wheel: area follows
 * the count, with a floor so a key with one tune is still a target. */
export function dotDiameter(count: number, max: number): number {
  if (count <= 0) return 0;
  return Math.max(3.4, 12 * Math.sqrt(count / Math.max(1, max)));
}

/** Where a slot sits, as left/top percentages of the wheel. */
export function slotPosition(slot: number, radius: number): { left: string; top: string } {
  const angle = ((slot * 30 - 90) * Math.PI) / 180;
  const round = (v: number) => Math.round(v * 100) / 100;
  return {
    left: `${round(50 + radius * Math.cos(angle))}%`,
    top: `${round(50 + radius * Math.sin(angle))}%`,
  };
}
